import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardBody, CardHeader, Input } from "reactstrap";
import { Link } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import { useQuery } from "@apollo/client";
import { GET_RECENTSUCCESSFUL_MESSAGES } from "./messages.query";
import "./shared-table.scss";

interface MessageData {
  Id: number;
  IsQueued: boolean;
  IsSent: boolean;
  IsDelivered: boolean;
  IsRead: boolean;
  IsDeleted: boolean;
  IsFailed: boolean;
  IsTemplate: boolean;
  OrgName: string;
  CreatedOn: string;
}

const Messages = () => {
  const [tableData, setTableData] = useState<MessageData[]>([]);
  const [searchText, setSearchText] = useState("");

  const columns = [
    {
      dataField: "Id",
      text: "ID",
      sort: true,
      headerStyle: { textAlign: "center" } as React.CSSProperties,
      style: { textAlign: "center" } as React.CSSProperties,
    },
    {
      dataField: "IsQueued",
      text: "Queued",
      sort: true,
      headerStyle: { textAlign: "center" } as React.CSSProperties,
      style: { textAlign: "center" } as React.CSSProperties,
      formatter: (cell: boolean) => (
        <i
          className={`mdi mdi-${
            cell ? "check-circle text-success" : "close-circle text-danger"
          }`}
        />
      ),
    },
    {
      dataField: "IsSent",
      text: "Sent",
      sort: true,
      headerStyle: { textAlign: "center" } as React.CSSProperties,
      style: { textAlign: "center" } as React.CSSProperties,
      formatter: (cell: boolean) => (
        <i
          className={`mdi mdi-${
            cell ? "check-circle text-success" : "close-circle text-danger"
          }`}
        />
      ),
    },
    {
      dataField: "IsDelivered",
      text: "Delivered",
      sort: true,
      headerStyle: { textAlign: "center" } as React.CSSProperties,
      style: { textAlign: "center" } as React.CSSProperties,
      formatter: (cell: boolean) => (
        <i
          className={`mdi mdi-${
            cell ? "check-circle text-success" : "close-circle text-danger"
          }`}
        />
      ),
    },
    {
      dataField: "IsRead",
      text: "Read",
      sort: true,
      headerStyle: { textAlign: "center" } as React.CSSProperties,
      style: { textAlign: "center" } as React.CSSProperties,
      formatter: (cell: boolean) => (
        <i
          className={`mdi mdi-${
            cell ? "check-circle text-success" : "close-circle text-danger"
          }`}
        />
      ),
    },
    {
      dataField: "IsDeleted",
      text: "Deleted",
      sort: true,
      headerStyle: { textAlign: "center" } as React.CSSProperties,
      style: { textAlign: "center" } as React.CSSProperties,
      formatter: (cell: boolean) => (
        <i
          className={`mdi mdi-${
            cell ? "check-circle text-success" : "close-circle text-danger"
          }`}
        />
      ),
    },
    {
      dataField: "IsFailed",
      text: "Failed",
      sort: true,
      headerStyle: { textAlign: "center" } as React.CSSProperties,
      style: { textAlign: "center" } as React.CSSProperties,
      formatter: (cell: boolean) => (
        <i
          className={`mdi mdi-${
            cell ? "check-circle text-success" : "close-circle text-danger"
          }`}
        />
      ),
    },
    {
      dataField: "IsTemplate",
      text: "Template",
      sort: true,
      headerStyle: { textAlign: "center" } as React.CSSProperties,
      style: { textAlign: "center" } as React.CSSProperties,
      formatter: (cell: boolean) => (
        <i
          className={`mdi mdi-${
            cell ? "check-circle text-success" : "close-circle text-danger"
          }`}
        />
      ),
    },
    {
      dataField: "OrgName",
      text: "Organization",
      sort: true,
    },
    {
      dataField: "CreatedOn",
      text: "Created",
      sort: true,
      sortFunc: (a: string, b: string, order: string) => {
        const dateA = parseDateTime(a);
        const dateB = parseDateTime(b);
        if (order === "asc") {
          return dateA - dateB;
        }
        return dateB - dateA;
      },
    },
  ];

  const defaultSorted: [{ dataField: string; order: "asc" | "desc" }] = [
    {
      dataField: "Id",
      order: "desc",
    },
  ];

  const pageOptions = {
    sizePerPage: 10,
    totalSize: tableData.length,
    custom: true,
    showTotal: true,
    paginationTotalRenderer: (from: number, to: number, size: number) => {
      return (
        <span className="react-bootstrap-table-pagination-total">
          Showing {from} to {to} of {size} Results
        </span>
      );
    },
  };

  const { data, refetch } = useQuery(GET_RECENTSUCCESSFUL_MESSAGES);

  const parseDateTime = (dateTimeStr: string): number => {
    const [datePart, timePart] = dateTimeStr.split(", ");
    const [day, month, year] = datePart.split("/").map(Number);
    const [hours, minutes, seconds] = timePart.split(":").map(Number);

    return new Date(year, month - 1, day, hours, minutes, seconds).getTime();
  };

  useEffect(() => {
    if (data && data.msgbox_Message) {
      const tmpData = data.msgbox_Message
        .filter((item: any) => item.Organisation)
        .map((item: any) => ({
          Id: item.MessageId,
          CreatedOn: new Date(item.CreatedOn).toLocaleString(),
          OrgName: item.Organisation.Name,
          IsQueued: item.IsQueued,
          IsDelivered: item.IsDelivered,
          IsSent: item.IsSent,
          IsRead: item.IsRead,
          IsFailed: item.IsFailed,
          IsDeleted: item.IsDeleted,
          IsTemplate: item.IsTemplate,
        }));
      setTableData(tmpData);
    }
  }, [data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setSearchText(text);

    if (data && data.msgbox_Message) {
      const filteredData = data.msgbox_Message
        .filter((item: any) => {
          const searchStr = text.toLowerCase();
          return (
            (item.Organisation?.Name || "").toLowerCase().includes(searchStr) ||
            item.MessageId.toString().includes(searchStr)
          );
        })
        .map((item: any) => ({
          Id: item.MessageId,
          CreatedOn: new Date(item.CreatedOn).toLocaleString(),
          OrgName: item.Organisation.Name,
          IsQueued: item.IsQueued,
          IsDelivered: item.IsDelivered,
          IsSent: item.IsSent,
          IsRead: item.IsRead,
          IsFailed: item.IsFailed,
          IsDeleted: item.IsDeleted,
          IsTemplate: item.IsTemplate,
        }));
      setTableData(filteredData);
    }
  };

  const refetchData = () => {
    refetch();
  };

  return (
    <React.Fragment>
      <Card>
        <CardHeader>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <h4 className="card-title">Recent Successful Messages</h4>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex flex-wrap align-items-start justify-content-md-end mt-2 mt-md-0 gap-2 mb-3">
                <div>
                  <Link to="#" className="btn btn-light" onClick={refetchData}>
                    <i className="uil uil-refresh me-1"></i> Refresh
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          <PaginationProvider pagination={paginationFactory(pageOptions)}>
            {({ paginationProps, paginationTableProps }) => (
              <React.Fragment>
                <Row className="mb-2">
                  <Col md="4">
                    <div className="search-box me-2 mb-2 d-inline-block">
                      <div className="position-relative">
                        <Input
                          type="text"
                          className="form-control"
                          placeholder="Search..."
                          value={searchText}
                          onChange={handleSearch}
                        />
                        <i className="bx bx-search-alt search-icon" />
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col xl="12">
                    <div className="table-responsive">
                      <BootstrapTable
                        bordered={false}
                        striped={false}
                        defaultSorted={defaultSorted}
                        classes="table align-middle table-nowrap"
                        headerWrapperClasses="thead-dark"
                        headerClasses="table-dark"
                        {...paginationTableProps}
                        keyField="Id"
                        columns={columns}
                        data={tableData}
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col className="inner-custom-pagination d-flex">
                    <div className="d-inline">
                      <SizePerPageDropdownStandalone {...paginationProps} />
                    </div>
                    <div className="text-md-right ms-auto">
                      <PaginationListStandalone {...paginationProps} />
                    </div>
                  </Col>
                </Row>
              </React.Fragment>
            )}
          </PaginationProvider>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default Messages;
